import React, { useEffect, useState } from "react";
import { graphql, useFragment } from "react-relay";
import { Link } from "react-router-dom";

import cx from "classnames";
import { differenceInDays, format } from "date-fns";
import { motion, type MotionProps } from "framer-motion";
import uuidv4 from "uuid/v4";

import PostUpgradeModal from "~/account/Billing/PostUpgradeModal";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { isNative } from "~/lib/utils";
import { Button, LinkButton, Tooltip, useModals } from "~/ui";
import { HelpCenterLink } from "~/ui/legacyFellowIndex";

import { UpgradeIcon } from "../billing";
import { ProductFamilies, TRIAL_END_HELP_ARTICLE_ID } from "../billing/utils";

import type {
	UpgradeLink_viewer$data as Viewer,
	UpgradeLink_viewer$key as ViewerKey,
} from "~/__generated__/UpgradeLink_viewer.graphql";

import navStyles from "./nav.module.css";
import styles from "./UpgradeLink.module.css";

const analyticsProperties = {
	fingerprint: uuidv4(),
	ux: "flyout",
	feature: "navbar_flyout",
};

interface NavbarUpgradePopupProps {
	account: Viewer["account"];
}

function NavbarUpgradePopup({ account }: NavbarUpgradePopupProps) {
	const {
		subscriptionProductName,
		subscriptionState,
		subscriptionStartDate,
		subscriptionTrialEnd,
		subscriptionTrialIsSoft,
		restrictedMetrics,
		noteLimit,
	} = account.billingAccount;

	const modals = useModals();

	const onLearnClick = React.useCallback(() => {
		dispatchAnalyticsTrack("Paywall Action Taken", { ...analyticsProperties, action: "redirect_to_marketing" });
	}, []);

	const onUpgradeClick = React.useCallback(() => {
		dispatchAnalyticsTrack("Paywall Action Taken", { ...analyticsProperties, action: "redirect_to_billing" });
	}, []);

	const onReonboardingClick = React.useCallback(() => {
		dispatchAnalyticsTrack("Paywall Action Taken", { ...analyticsProperties, action: "redirect_to_re_onboarding" });
		modals.open(({ close }) => <PostUpgradeModal onClose={close} />);
	}, [modals]);

	React.useEffect(() => {
		dispatchAnalyticsTrack("Paywall Encountered", analyticsProperties);
	}, [subscriptionProductName, subscriptionStartDate, subscriptionState]);

	if (subscriptionState === "SUBSCRIBED") {
		return (
			<div className={styles.contents}>
				<div>
					<strong>Your workspace has been upgraded to {subscriptionProductName}!</strong>
					<br />
					<br />
					You now have access to new features, integrations, and much more. Take a look at what&apos;s new.
				</div>
				<Button className={styles.upgradeButton} variant="primary" onClick={onReonboardingClick}>
					Show me
				</Button>
			</div>
		);
	}

	const upgradeButton = (
		<LinkButton
			to="/account/billing/plan/?from=upgrade"
			onClick={onUpgradeClick}
			variant="primary"
			className={styles.upgradeButton}
		>
			Upgrade now
		</LinkButton>
	);

	if (subscriptionState === "TRIAL" && subscriptionTrialEnd) {
		const trialEnd = new Date(subscriptionTrialEnd);
		const dayDiff = Math.max(0, differenceInDays(trialEnd, new Date()));

		return (
			<div className={styles.contents}>
				<div>
					Your <strong>{subscriptionProductName}</strong> trial has{" "}
					<strong>
						<i>{dayDiff} days remaining.</i>
					</strong>{" "}
					On {format(trialEnd, "MMM d, yyyy")}, your workspace will{" "}
					{subscriptionTrialIsSoft ? "revert to its original plan" : "be downgraded to our free plan"}.{" "}
					<HelpCenterLink className="invert-theme" articleId={TRIAL_END_HELP_ARTICLE_ID} onClick={onLearnClick}>
						Learn more
					</HelpCenterLink>
				</div>
				{upgradeButton}
			</div>
		);
	}

	const { feedbackCount, noteCount } = restrictedMetrics;

	let missingText = "";
	if (feedbackCount > 0 && noteCount > 0) {
		missingText = `You are missing out on ${noteCount} notes and ${feedbackCount} feedback items by not upgrading.`;
	} else if (feedbackCount > 0) {
		missingText = `You are missing out on ${feedbackCount} feedback items by not upgrading.`;
	} else if (noteCount > 0) {
		missingText = `You are missing out on ${noteCount} notes by not upgrading.`;
	}

	if (subscriptionState === "POST_TRIAL" && subscriptionTrialEnd) {
		const trialEnd = new Date(subscriptionTrialEnd);

		return (
			<div className={styles.contents}>
				<div>
					Your free trial has{" "}
					<strong>
						<i>expired.</i>
					</strong>{" "}
					As of {format(trialEnd, "MMM d, yyyy")}, your workspace has been downgraded to our free plan.{" "}
					{missingText && <div className={styles.restrictedMetric}>{missingText}</div>}
					<HelpCenterLink className="invert-theme" articleId={TRIAL_END_HELP_ARTICLE_ID} onClick={onLearnClick}>
						Learn more
					</HelpCenterLink>
				</div>
				{upgradeButton}
			</div>
		);
	}

	return (
		<div className={styles.contents}>
			<div>
				<div>
					<span>
						You are currently on the <i>Free plan</i>. You do not have access to notes and feedback created over{" "}
						<strong>{noteLimit} days ago</strong>, as well as Fellow&apos;s Team features.
						{missingText && (
							<>
								<br />
								<br />
								{missingText}
							</>
						)}
					</span>
				</div>
				<HelpCenterLink
					className="invert-theme"
					articleId={ProductFamilies["PRO"].helpArticleId}
					onClick={onLearnClick}
				>
					Learn more
				</HelpCenterLink>
			</div>
			{upgradeButton}
		</div>
	);
}

const theme = document.documentElement.dataset.theme;

const progressArcVariants: MotionProps["variants"] = {
	visible: ({ percentage, diff }: { percentage: number; diff: number }) => ({
		strokeDasharray: `${percentage * 0.93} ${113 - percentage * 0.93}`,
		stroke: diff < 4 ? "#D44243" : "#FCBF12",
		transition: {
			ease: "easeInOut",
			duration: 0.8,
			delay: 1,
		},
	}),
	hidden: {
		strokeDasharray: "0 113",
		stroke: "#FCBF12",
	},
};

const trialArcVariants = {
	visible: ({ diff }: { diff: number }) => ({
		stroke: theme == "theme-dark" ? "#4E462B" : diff < 4 ? "#FFCCCC" : "#FFE9B3",
		transition: { ease: "easeInOut", duration: 0.8, delay: 1 },
	}),
	hidden: {
		stroke: theme == "theme-dark" ? "#4E462B" : "#FFE9B3",
	},
};

const CircleProgressBarBase = ({
	width,
	diff,
	percentage,
	className,
}: {
	width: number;
	diff: number;
	percentage: number;
	className: string;
}) => {
	const [animate, setAnimate] = useState("hidden");

	const circleProps = {
		animate,
		custom: { diff, percentage },
		cx: width / 2,
		cy: width / 2,
		r: 18,
		fill: "transparent",
		strokeWidth: 4,
		strokeLinecap: "round" as const,
	};

	useEffect(() => {
		window.setTimeout(() => setAnimate("visible"), 0);
	}, [setAnimate]);

	return (
		<figure className={className} style={{ height: `${width}px` }}>
			<svg viewBox={`0 0 ${width} ${width}`} width={width} height={width}>
				<motion.circle
					initial="hidden"
					variants={trialArcVariants}
					strokeDasharray="80 20"
					style={{ transform: "rotate(163deg)" }}
					{...circleProps}
				/>
				<motion.circle
					initial="hidden"
					variants={progressArcVariants}
					strokeDasharray="80 20"
					style={{ transform: "rotate(122deg)" }}
					{...circleProps}
				/>
			</svg>
		</figure>
	);
};

interface UpgradeLinkProps {
	viewer: ViewerKey;
	className?: string;
}

export default function UpgradeLink(props: UpgradeLinkProps) {
	const { account } = useFragment(
		graphql`
			fragment UpgradeLink_viewer on Viewer {
				account {
					id
					billingAccount {
						id
						subscriptionState
						subscriptionProductName
						subscriptionStartDate
						subscriptionTrialEnd
						subscriptionTrialLength
						subscriptionTrialIsSoft
						restrictedMetrics
						noteLimit: usageLimitForFeature(feature: history)
					}
				}
			}
		`,
		props.viewer,
	);

	const { subscriptionState, subscriptionTrialEnd, subscriptionTrialLength } = account.billingAccount;

	if (isNative) return null;

	let trigger = (
		<Link to="/account/billing/plan/" className={navStyles.navButton} data-test-id="navbar-upgrade-link">
			<div className={navStyles.navButtonIconWrapper} aria-hidden>
				<UpgradeIcon className={styles.upgradeIcon} />
			</div>
			<div className={cx(navStyles.navButtonLabel, styles.navButtonLabel)}>Upgrade</div>
		</Link>
	);

	if (subscriptionState === "SUBSCRIBED") {
		trigger = (
			<Link to="/account/billing/plan/" className={navStyles.navButton} data-test-id="navbar-upgrade-link">
				<div className={navStyles.navButtonIconWrapper} aria-hidden>
					<UpgradeIcon className={styles.upgradeIcon} />
				</div>
				<div className={cx(navStyles.navButtonLabel, styles.navButtonLabel, styles.longLabel)}>
					New
					<br />
					Features
				</div>
			</Link>
		);
	}

	if (subscriptionState === "TRIAL" && subscriptionTrialEnd) {
		const trialEnd = new Date(subscriptionTrialEnd);
		const dayDiff = Math.max(0, differenceInDays(trialEnd, new Date()));

		const progressProps = {
			width: 40,
			diff: dayDiff,
			className: styles.progressBar,
		};

		trigger = (
			<Link to="/account/billing/plan/" className={styles.progressBarButton} data-test-id="navbar-upgrade-link">
				<div style={{ height: "8px" }}>&nbsp;</div>
				<div className={styles.days}>{dayDiff}</div>
				<div className={styles.plan}>&nbsp;</div>
				<CircleProgressBarBase
					percentage={((subscriptionTrialLength - dayDiff) / subscriptionTrialLength) * 100}
					{...progressProps}
				/>
			</Link>
		);
	}

	return (
		<Tooltip
			placement="right"
			className={styles.tooltip}
			interactive
			content={<NavbarUpgradePopup account={account} />}
		>
			{trigger}
		</Tooltip>
	);
}
